import React, { useCallback, useMemo, useState } from 'react';
import { Image, ImageStyle, LayoutChangeEvent, Pressable, StyleSheet } from 'react-native';
import {
  RecyclerListView,
  DataProvider,
  LayoutProvider,
  RecyclerListViewProps,
} from 'recyclerlistview';
import { DNABox, DNAText, Iffy } from '@alucio/lux-ui';
import DNAThumbnail from 'src/components/DNA/Thumbnail/DNAThumbnail';
import { useAppSettings } from 'src/state/context/AppSettings';
import useSelector, { composite } from 'src/hooks/useSelector';
import * as relatedSlideSelector from 'src/state/machines/relatedSlide/relatedSlide.selectors';
import { relatedSlideRenderData, useRelatedSlide } from 'src/state/machines/relatedSlide/RelatedSlideProvider';
import { detectArchivedFileKeyPath } from 'src/components/SlideSelector/useThumbnailSelector';
import { tabConfigs } from 'src/components/ContentPreviewModalV2/SideBar';
import SidebarContent from 'src/components/ContentPreviewModalV2/SideBar/SideBarContent';
import DNAError from 'src/components/DNA/Error/DNAError';
import {
  SLIDE_ROLL_VARIANT,
  thumbnailVariantSettings,
  thumbnailVariantStyles,
} from 'src/components/DNA/SlideRoll/StyleSetting';

const LoadingGridImg = require('../../../../../assets/images/LoadingGrid.gif');

enum ItemType {
  SLIDE,
}

const NO_RESULT_MESSAGE = 'No related slides';
const OFFLINE_MESSAGE = 'Please connect to the internet to see content';
const ERROR_MESSAGE = 'Sorry, we’re unable to load the slides. Please try again.';

export const styles = StyleSheet.create({
  parentContainer: {
    minHeight: '100%',
    minWidth: '100%',
  },
  loadingImage: {
    width: 100,
    height: 100,
  },
  SubText: {
    textAlign: 'center',
    lineHeight: 24,
  },
  flex: {
    flex: 1,
  },
});

export const RelatedSlides: React.FC = () => {
  const {
    service,
    scrollViewRef,
    currentRelatedSlidesData,
    onErrorRetry,
    onSelectedRelatedSlide,
  } = useRelatedSlide();
  const { deviceMode } = useAppSettings();
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const isTablet = deviceMode === 'tablet';

  const cond = useSelector(
    service,
    (state) => composite(
      state,
      relatedSlideSelector.isLoading,
      relatedSlideSelector.isPresentingResult,
      relatedSlideSelector.isPresentingNoResult,
      relatedSlideSelector.isOfflineState,
      relatedSlideSelector.isErrorState,
      relatedSlideSelector.currentPageId,
    ),
  )

  const variantStyles = useMemo(() => thumbnailVariantStyles[SLIDE_ROLL_VARIANT.PREVIEW_MODAL], []);
  const variantSettings = useMemo(() => thumbnailVariantSettings[SLIDE_ROLL_VARIANT.PREVIEW_MODAL], []);
  const dataProviderInit = useMemo(() => new DataProvider((r1: string, r2: string) => r1 !== r2), []);

  // ROW DATA
  const dataProvider = useMemo(() => dataProviderInit.cloneWithRows([...currentRelatedSlidesData]),
    [currentRelatedSlidesData]);

  const layoutProvider = useMemo(() => {
    const _layoutProvider = new LayoutProvider((_idx) => ItemType.SLIDE,
      (type, dim, _idx) => {
        switch (type) {
          case ItemType.SLIDE: {
            dim.width = containerWidth;
            dim.height = variantSettings.thumbnailHeight + 36; // ADDING GAP AS PADDING
            break;
          }
        }
      })
    _layoutProvider.shouldRefreshWithAnchoring = false;
    return _layoutProvider;
  }, [containerWidth, variantSettings]);

  const rowRenderer = useCallback<RecyclerListViewProps['rowRenderer']>(
    (type, data, _idx) => {
      const { documentVersionORM, page, thumbnailTitle } = data as relatedSlideRenderData;
      const s3URL = detectArchivedFileKeyPath(documentVersionORM.model, page, isTablet ? 'sm' : 'xl');

      switch (type) {
        case ItemType.SLIDE: {
          return (
            <DNABox fill alignX="center" alignY="center">
              <Pressable onPress={() => onSelectedRelatedSlide(documentVersionORM, page.number)}>
                <DNABox appearance="col" spacing="xs">
                  <DNAThumbnail.Lazy
                    s3URL={s3URL}
                    onCheck={() => onSelectedRelatedSlide(documentVersionORM, page.number)}
                    pageNumber={page.number}
                    preview={{
                      pages: documentVersionORM.meta.allPages,
                      docVerORM: documentVersionORM,
                      getSlideTitle: (pageNumber: number) => (
                        pageNumber === page.number ? thumbnailTitle : ''
                      ),
                    }}
                    magnifyConfig={DNAThumbnail.MagnifyPresets.SINGLE}
                    overlayText={documentVersionORM.model.title}
                    thumbnailTitle={thumbnailTitle || undefined}
                    height={variantSettings.thumbnailHeight}
                    width={variantSettings.thumbnailWidth}
                    style={variantStyles.thumbnail as ImageStyle}
                  />
                </DNABox>
              </Pressable>
            </DNABox>
          )
        }
        default: {
          return null
        }
      }
    },
    [layoutProvider, dataProvider])

  const handleContainerLayout = useCallback((e: LayoutChangeEvent) => {
    const containerWidth = e.nativeEvent.layout.width;
    setContainerWidth(containerWidth);
  }, []);

  return (
    <DNABox style={styles.parentContainer} onLayout={handleContainerLayout}>
      {/* LOADING */}
      <Iffy is={cond.isLoading}>
        <DNABox fill appearance="col" spacing="lg" alignX="center">
          {[...Array(3)].map((_, index) => (
            <Image
              key={`loading-grid-${index}`}
              source={LoadingGridImg}
              style={styles.loadingImage}
              resizeMode="contain"
            />
          ))}
        </DNABox>
      </Iffy>
      {/* PRESNETING - DISPLAY SLIDE(S) */}
      <Iffy is={cond.isPresentingResult && cond.currentPageId}>
        <RecyclerListView
          key={cond.currentPageId}
          ref={scrollViewRef}
          dataProvider={dataProvider}
          layoutProvider={layoutProvider}
          rowRenderer={rowRenderer}
          style={styles.flex}
          scrollViewProps={{ scrollEnabled: false }}
        />
      </Iffy>
      {/* PRESNETING - NO RESULT */}
      <Iffy is={cond.isPresentingNoResult}>
        <DNABox fill alignX="center" alignY="center">
          <DNAText status="subtle" numberOfLines={3} style={styles.SubText}>{NO_RESULT_MESSAGE}</DNAText>
        </DNABox>
      </Iffy>
      {/* OFFLINE */}
      <Iffy is={cond.isOfflineState}>
        <DNABox fill alignX="center" alignY="center">
          <DNAText status="subtle" numberOfLines={3} style={styles.SubText}>{OFFLINE_MESSAGE}</DNAText>
        </DNABox>
      </Iffy>
      {/* ERROR */}
      <Iffy is={cond.isErrorState}>
        <DNAError
          message={ERROR_MESSAGE}
          actionButtonText="Try again"
          actionButtonOnPress={onErrorRetry}
        />
      </Iffy>
    </DNABox>
  )
}

const RelatedSlidesWrapper: React.FC = () => {
  return (
    <SidebarContent>
      <SidebarContent.Header sidebarHeader={tabConfigs.relatedSlides.label}/>
      <SidebarContent.Body>
        <RelatedSlides />
      </SidebarContent.Body>
    </SidebarContent>
  )
}

export default RelatedSlidesWrapper;
